import { host, convertToJpeg } from './variables';

const CHUNK_SIZE = 10 * 1024 * 1024; // 10 MB — also S3 multipart threshold

const FOLDER_IMAGE_SETTINGS = {
  dish:       { max: 1200, quality: 0.82 },
  menu:       { max: 1200, quality: 0.82 },
  restaurant: { max: 1600, quality: 0.85 },
  moment:     { max: 1400, quality: 0.85 },
  thumbnail:  { max:  800, quality: 0.80 },
  document:   { max: 1600, quality: 0.85 },
  inventory:  { max: 1200, quality: 0.82 },
};

const ACCEPTED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/heic'];

//  Helpers

const normalizeImage = async (file, folder) => {
  if (!ACCEPTED_IMAGE_TYPES.includes(file.type)) return file;
  const settings = FOLDER_IMAGE_SETTINGS[folder];
  if (!settings) return file;
  if (file.type === 'image/jpeg' && file.size < 300 * 1024) return file;
  const blob = await convertToJpeg(file, settings.quality, settings.max);
  return new File([blob], file.name.replace(/\.[^.]+$/, '.jpg'), { type: 'image/jpeg' });
};

const computeHash = async (file) => {
  const buffer      = await file.arrayBuffer();
  const hashBuffer  = await crypto.subtle.digest('SHA-256', buffer);
  return Array.from(new Uint8Array(hashBuffer))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
};

const getToken = () =>
  localStorage.getItem('partnertoken') || localStorage.getItem('token');

const apiPost = async (path, body, token) => {
  const res = await fetch(`${host}${path}`, {
    method:  'POST',
    headers: { 'Content-Type': 'application/json', Authorization: token },
    body:    JSON.stringify(body),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.msg || `Request to ${path} failed (${res.status})`);
  }
  return res.json();
};

// XHR PUT for a single file (< 10 MB)
const xhrPut = (url, data, contentType, onProgress) =>
  new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', url);
    xhr.setRequestHeader('Content-Type', contentType);
    xhr.timeout = 10 * 60 * 1000;
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100));
    };
    xhr.onload    = () => (xhr.status === 200 ? resolve(xhr) : reject(new Error(`Upload failed (${xhr.status})`)));
    xhr.onerror   = () => reject(new Error('Network error during upload.'));
    xhr.ontimeout = () => reject(new Error('Upload timed out.'));
    xhr.send(data);
  });

// XHR PUT for one multipart chunk — resolves with the ETag header value
const xhrPutPart = (url, chunk, onProgress) =>
  new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', url);
    xhr.timeout = 15 * 60 * 1000;
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100));
    };
    xhr.onload = () => {
      if (xhr.status === 200) {
        resolve(xhr.getResponseHeader('ETag'));
      } else {
        reject(new Error(`Part upload failed (${xhr.status})`));
      }
    };
    xhr.onerror   = () => reject(new Error('Network error during part upload.'));
    xhr.ontimeout = () => reject(new Error('Part upload timed out.'));
    xhr.send(chunk);
  });

//  Single-part path (<= 10 MB)

const singlePartUpload = async (file, hash, ext, folder, cafeId, token, onProgress) => {
  const { uploadUrl, fileKey, publicUrl } = await apiPost('/upload/v2/presign', {
    hash, ext, folder, cafeId, fileType: file.type,
  }, token);

  await xhrPut(uploadUrl, file, file.type, onProgress);
  await apiPost('/upload/v2/presign-done', { hash, fileKey }, token);

  return { publicUrl, fileKey };
};

//  Multipart path (> 10 MB)

const multipartUpload = async (file, hash, ext, folder, cafeId, token, onProgress, checkResult) => {
  const totalParts = Math.ceil(file.size / CHUNK_SIZE);

  let sessionId, uploadId, fileKey, completedParts = [];

  if (checkResult.status === 'resumable') {
    ({ sessionId, uploadId, fileKey, completedParts } = checkResult);
  } else {
    const init = await apiPost('/upload/v2/multipart/init', {
      hash, ext, folder, cafeId, fileType: file.type, totalParts,
    }, token);
    ({ sessionId, uploadId, fileKey } = init);
  }

  const doneSet      = new Set(completedParts.map((p) => p.partNumber));
  const pendingNums  = Array.from({ length: totalParts }, (_, i) => i + 1).filter((n) => !doneSet.has(n));

  const { parts: presignedParts } = await apiPost('/upload/v2/multipart/presign-parts', {
    uploadId, fileKey, partNumbers: pendingNums,
  }, token);

  // Track bytes across all parts for a smooth global progress bar
  const bytesAlreadyDone = completedParts.length * CHUNK_SIZE;
  let bytesUploaded = bytesAlreadyDone;

  const allParts = [...completedParts];

  for (const { partNumber, url } of presignedParts) {
    const start = (partNumber - 1) * CHUNK_SIZE;
    const chunk = file.slice(start, start + CHUNK_SIZE);

    const rawEtag = await xhrPutPart(url, chunk, (partPct) => {
      const partBytes  = chunk.size * (partPct / 100);
      const totalBytes = bytesUploaded + partBytes;
      onProgress(Math.min(99, Math.round((totalBytes / file.size) * 100)));
    });
    const etag = rawEtag ? rawEtag.replace(/"/g, '') : rawEtag;

    bytesUploaded += chunk.size;
    allParts.push({ partNumber, etag });

    // Persist per-part progress so a resume can skip this chunk
    await apiPost('/upload/v2/multipart/part-done', { sessionId, partNumber, etag }, token).catch(() => {});
  }

  const { publicUrl } = await apiPost('/upload/v2/multipart/complete', {
    sessionId, uploadId, fileKey, parts: allParts,
  }, token);

  onProgress(100);
  return { publicUrl, fileKey };
};

//  Public API

// Shared core — returns { publicUrl, fileKey }
const uploadCore = async (file, folder, onProgress, cafeId) => {
  const normalized = await normalizeImage(file, folder);
  const hash       = await computeHash(normalized);
  const ext        = normalized.name.split('.').pop().toLowerCase();
  const token      = getToken();

  const checkResult = await apiPost('/upload/v2/check', { hash, ext, folder, cafeId }, token);

  if (checkResult.status === 'exists') {
    onProgress(100);
    return { publicUrl: checkResult.publicUrl, fileKey: checkResult.fileKey };
  }

  if (normalized.size <= CHUNK_SIZE) {
    return singlePartUpload(normalized, hash, ext, folder, cafeId, token, onProgress);
  }

  return multipartUpload(normalized, hash, ext, folder, cafeId, token, onProgress, checkResult);
};

/**
 * Drop-in replacement for uploadViaPresign — returns the public URL string.
 * Image normalisation (resize + JPEG conversion) is preserved.
 * Adds: SHA-256 deduplication, resumable chunked upload for files > 10 MB.
 */
const uploadViaPresignV2 = (file, folder, onProgress = () => {}, cafeId = null) =>
  uploadCore(file, folder, onProgress, cafeId).then(({ publicUrl }) => publicUrl);

/**
 * Same as uploadViaPresignV2 but returns { publicUrl, fileKey }.
 * Use this when the caller needs the S3 key (e.g. to trigger server-side AI processing).
 */
export const uploadAndGetKeyV2 = (file, folder, onProgress = () => {}, cafeId = null) =>
  uploadCore(file, folder, onProgress, cafeId);

export { xhrPut as xhrUpload };
export default uploadViaPresignV2;
